import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';

import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';

import { List, Record as ImRecord } from 'immutable';

import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';

import { useAppSelector } from 'app-components/hooks/redux';
import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import { accountDiscoveryActions } from 'app-components/flux/account-discovery';
import { accountsTypes } from 'app-components/flux/accounts';
import { actionHelpers } from 'app-components/flux/core';
import { institutionLoginsSelectors, institutionLoginsTypes } from 'app-components/flux/institution-logins';
import { getLogger } from 'app-components/utils/core';
import { institutionsUtils } from 'app-components/flux/institutions';

import NewOrDiscoverForm from '../NewOrRediscoverPicker';

import FinicityConnect from './FinicityConnect';
import PlaidLink from './PlaidLink';
import StripeOAuth from './StripeOAuth';

const log = getLogger('WidgetAuthenticator/index.js');

const mkCredentialsList = (aggregator, cpSuccessData) => {
  let credentials;
  switch (aggregator) {
    case 'PLAID':
      credentials = List([institutionLoginsTypes.mkCredential({ key: 'public-token', value: cpSuccessData.token })]);
      break;
    case 'STRIPE':
      // TODO: should we add state here
      credentials = List([institutionLoginsTypes.mkCredential({ key: 'code', value: cpSuccessData.code })]);
      break;
    case 'FINICITY':
    default:
      credentials = List([institutionLoginsTypes.mkCredential({ key: 'DUMMY', value: 'DUMMY' })]);
      break;
  }
  return credentials;
};

const mkNewInstitutionLogin = (aggregator, institution, cpSuccessData, overrides = {}) => {
  return institutionLoginsTypes.mkInstitutionLoginClient({
    cpSetupMode: 'DISCOVER_ACCOUNTS_ONLY',
    institutionId: institution.id.startsWith('STATIC_') ? null : institution.id,
    channel: cpSuccessData.channel,
    name: institution.name,
    credentials: mkCredentialsList(aggregator, cpSuccessData),
    aggregators: List([
      accountsTypes.mkAccountAggregator({
        ...(cpSuccessData.cpId && { cpId: cpSuccessData.cpId }),
        channel: cpSuccessData.channel,
      }),
    ]),
    ...overrides,
  });
};

export function transformDiscoveredAccountsToAccounts(channel, discoveredAccounts) {
  return discoveredAccounts.reduce((list, discoveredAccount) => {
    const aggregatorParams = {
      cpId: discoveredAccount.cpId,
      cpLoginId: discoveredAccount.cpLoginId,

      channel: discoveredAccount.channel,

      subType: discoveredAccount.subtype,

      accountName: discoveredAccount.name,
      accountNumberMasked: discoveredAccount.maskedNumber,
    };
    const aggregators = List([accountsTypes.mkAccountAggregator(aggregatorParams)]);

    list.push(accountsTypes.mkAccount(discoveredAccount.type, { aggregators }));
    return list;
  }, []);
}

function transformDiscoveredAccountsError(error) {
  if (error.response?.data?.errors) {
    const qcsError = error.response.data.errors[0];
    return {
      statusCode: qcsError.code,
      text: qcsError.title,
    };
  }
  return null;
}

const WidgetAuthenticator = forwardRef((props, ref) => {
  const {
    aggregator,
    fiBrandingIndent,
    institution,
    institutionLoginToUpdate,
    onBack,
    reauthenticate,
    scope,
    trackingProperties,
    fixFiMode,
    businessId,
  } = props;
  const dispatch = useDispatch();

  const pageRef = useRef(null);

  const [cpSuccessData, setCpSuccessData] = useState();
  const [error, setError] = useState();
  const [page, setPage] = useState('WIDGET');
  const [userSelectedInstitutionLogin, setUserSelectedInstitutionLogin] = useState();

  const institutionLoginsById = useAppSelector(institutionLoginsSelectors.getInstitutionLoginsById);

  // If aggregator does not guarantee us that a duplicate add will result in the previous login being returned then we
  // must let user confirm they want a new login or they choose an existing login to update.
  //
  const haveUserConfirmNewLogin = aggregator === 'PLAID';

  useImperativeHandle(ref, () => ({
    handleBack: () => {
      if (pageRef.current?.handleBack) {
        pageRef.current?.handleBack();
      } else {
        onBack();
      }
    },
  }));

  const doDiscoverWithPolling = useCallback(
    async (institutionLogin, pollingRef) => {
      const { cpSetupMode, credentials } = institutionLogin;
      const discoverInstitutionLoginWithAccountsByPolling = actionHelpers.bindPromiseAction(
        dispatch,
        accountDiscoveryActions.discoverInstitutionLoginWithAccountsByPolling,
      );
      const response = await discoverInstitutionLoginWithAccountsByPolling({ institutionLogin, pollingRef }, { scope });
      response.institutionLogin = response.institutionLogin.merge({ cpSetupMode, credentials });
      return response;
    },
    [dispatch, scope],
  );

  const doDiscover = useCallback(
    async (institutionLogin, accounts) => {
      const { cpSetupMode, credentials } = institutionLogin;
      const discoverInstitutionLoginWithAccounts = actionHelpers.bindPromiseAction(
        dispatch,
        accountDiscoveryActions.discoverInstitutionLoginWithAccounts,
      );
      const response = await discoverInstitutionLoginWithAccounts(
        { institutionLogin, accounts },
        { scope, businessId },
      );
      response.institutionLogin = response.institutionLogin.merge({ cpSetupMode, credentials });
      return response;
    },
    [businessId, dispatch, scope],
  );

  const discover = useCallback(async () => {
    const { accounts, channel, existingLoginsAtInstitution, pollingRef } = cpSuccessData;
    if (userSelectedInstitutionLogin) {
      // If we have user specified institution (could be new), then we rediscover, set discover response
      // and use a REPLACE update strategy (institution-login and accounts will have their
      // aggregation data replaced with discovered data.
      const response = await doDiscover(userSelectedInstitutionLogin, accounts);
      dispatch(
        accountDiscoveryActions.discoverInstitutionLoginWithAccountsResponse(
          { ...response, updateStrategy: 'REPLACE' },
          { scope },
        ),
      );
    } else if (existingLoginsAtInstitution.length === 0) {
      // If we do not have any other logins to same institution, then we can get and set discover response. If we have an
      // institutionLoginToUpdate (may happen during UPDATE-FI), then update it instead of creating new institution-login.
      const overrides =
        institutionLoginToUpdate?.cpInstitutionId === cpSuccessData.cpInstitution?.id
          ? { id: institutionLoginToUpdate?.id }
          : {};
      const response = pollingRef
        ? await doDiscoverWithPolling(
            mkNewInstitutionLogin(aggregator, institution, cpSuccessData, overrides),
            pollingRef,
          )
        : await doDiscover(mkNewInstitutionLogin(aggregator, institution, cpSuccessData, overrides), accounts);
      const updateStrategy = response.institutionLogin.id ? 'UPDATE' : 'REPLACE';
      dispatch(
        accountDiscoveryActions.discoverInstitutionLoginWithAccountsResponse(
          { ...response, updateStrategy },
          { scope },
        ),
      );
    } else if (!pollingRef || !cpSuccessData.accounts) {
      // We potentially have an ambiguous login (unclear whether user is attempting to add new login or update an
      // existing one. This can happen even if we have an institutionLoginToUpdate.
      //
      // When we have a pollingRef, we may need to update the cpSuccessData. This update will cause the dependency to
      // change. So, we only run this code if we don't have a pollingRef or we don't have accounts in the cpSuccessData.
      //
      const response = pollingRef
        ? await doDiscoverWithPolling(mkNewInstitutionLogin(aggregator, institution, cpSuccessData), pollingRef)
        : await doDiscover(mkNewInstitutionLogin(aggregator, institution, cpSuccessData), accounts);
      const { institutionLogin } = response;
      if (institutionLogin.id || !haveUserConfirmNewLogin) {
        const updateStrategy = institutionLogin.id ? 'UPDATE' : 'REPLACE';
        dispatch(
          accountDiscoveryActions.discoverInstitutionLoginWithAccountsResponse(
            { ...response, updateStrategy },
            { scope },
          ),
        );
      } else {
        // first time we receive accounts, store them for rediscovery
        if (pollingRef) {
          const { accountDiscoveryData } = response;
          const newDiscoveredAccounts = transformDiscoveredAccountsToAccounts(
            channel,
            accountDiscoveryData.newAccounts,
          );

          setCpSuccessData({
            ...cpSuccessData,
            cpId: institutionLogin?.aggregators?.get(0).cpId,
            accounts: newDiscoveredAccounts,
          });
        }
        setPage('NEW_OR_REDISCOVER_PICKER');
        dispatch(accountDiscoveryActions.discoverInstitutionLoginWithAccountsDiscard(null, { scope }));
      }
    }
  }, [
    aggregator,
    cpSuccessData,
    dispatch,
    doDiscover,
    doDiscoverWithPolling,
    haveUserConfirmNewLogin,
    institution,
    institutionLoginToUpdate,
    scope,
    userSelectedInstitutionLogin,
  ]);

  useEffect(() => {
    if (!cpSuccessData) {
      return;
    }
    const doDiscover = async () => {
      try {
        await discover();
      } catch (error) {
        assert(false, error);
        log.error('Error in discovery', error);
        setCpSuccessData(null);
        setError(transformDiscoveredAccountsError(error));
        dispatch(accountDiscoveryActions.discoverInstitutionLoginWithAccountsResponse(error, { scope }));
      }
    };
    doDiscover();
  }, [discover, dispatch, cpSuccessData, scope]);

  const handleWidgetSuccess = (data) => {
    if (reauthenticate) {
      dispatch(
        accountDiscoveryActions.accountDiscoveryWidgetUpdateSuccess(
          { institutionLoginToUpdate, accountDiscoveryData: null },
          { scope },
        ),
      );
      return;
    }

    const existingLoginsAtInstitution = institutionLoginsById.reduce((list, login) => {
      if (login.isConnected && login.institutionId === institution.id) {
        list.push(login);
      }
      return list;
    }, []);

    setCpSuccessData({ ...data, existingLoginsAtInstitution });
  };

  const handleAdd = () => {
    // TODO: This forces a call to discover again. However, we really don't need to do another discovery if we
    //       save the previous discovery response. Instead, should we just trigger a successful discovery?
    setUserSelectedInstitutionLogin(mkNewInstitutionLogin(aggregator, institution, cpSuccessData));
  };

  const handleRediscover = (institutionLogin) => {
    setUserSelectedInstitutionLogin(
      institutionLogin.merge({
        cpSetupMode: 'DISCOVER_ACCOUNTS_ONLY',
        credentials: mkCredentialsList(aggregator, cpSuccessData),
      }),
    );
  };

  const renderFinicity = () => {
    if (!cpSuccessData) {
      return (
        <FinicityConnect
          institution={institution}
          onClosed={onBack}
          onSuccess={handleWidgetSuccess}
          reauthenticate={reauthenticate}
          scope={scope}
          trackingProperties={trackingProperties}
          fixFiMode={fixFiMode}
        />
      );
    }
    return (
      <Box display='flex' minHeight='200px'>
        <CircularProgress size={100} style={{ margin: 'auto' }} />
      </Box>
    );
  };

  const renderPlaid = () => {
    if (!cpSuccessData) {
      return (
        <PlaidLink
          institution={institution}
          onClosed={onBack}
          onSuccess={handleWidgetSuccess}
          reauthenticate={reauthenticate}
          scope={scope}
          trackingProperties={trackingProperties}
          fixFiMode={fixFiMode}
        />
      );
    }
    return null;
  };

  const renderStripe = () => {
    if (!cpSuccessData) {
      return (
        <StripeOAuth
          error={error}
          onSuccess={handleWidgetSuccess}
          scope={scope}
          trackingProperties={trackingProperties}
        />
      );
    }
    return <div>Still on Widget Authenticator Page</div>;
  };

  // it's just a POC 'plaidWidget' FF protected - not ready for production use
  const plaidWidget = useAppSelector((state) => featureFlagsSelectors.getFeatureFlag(state, 'plaidWidget'));
  const plaidEnv = useAppSelector((state) => featureFlagsSelectors.getFeatureFlag(state, 'plaidEnv'));
  useEffect(() => {
    if (page === 'WIDGET' && aggregator === 'PLAID' && plaidWidget) {
      const channel = institutionsUtils.getDefaultChannel(institution, ImRecord({ plaidEnv: undefined })({ plaidEnv }));
      new Promise((resolve, reject) =>
        dispatch(
          accountDiscoveryActions.getAggregationInfo(
            {
              institution,
              channel,
            },
            { resolve, reject },
          ),
        ),
      )
        .then((aggregationInfo) => {
          assert(aggregationInfo.connectUrl, 'Missing connectUrl in aggregationInfo', aggregationInfo);
          const url = new URL(aggregationInfo.connectUrl);
          url.searchParams.set('channel', channel); // TODO: QCS should cache channel
          if (window.location.hostname === 'localhost') {
            url.hostname = 'localhost';
            url.port = '8000';
            url.protocol = 'http:';
          }
          window.open(url.toString(), '_blank', 'noopener,noreferrer');

          const institutionLogin = mkNewInstitutionLogin(aggregator, institution, { channel });
          new Promise((resolve, reject) =>
            dispatch(
              accountDiscoveryActions.discoverInstitutionLoginWithAccountsByPolling(
                { institutionLogin, pollingRef: aggregationInfo.pollingRef },
                { resolve, reject },
              ),
            ),
          )
            .then((response) => {
              log.info('Plaid account discovery complete', JSON.stringify(response.accountDiscoveryData, null, 2));
              const institutionLoginWithDiscoverId = institutionLogin.set(
                'credentials',
                List([
                  institutionLoginsTypes.mkCredential({
                    key: 'discover_login_id',
                    value: response.institutionLogin.id,
                  }),
                ]),
              );
              dispatch(
                accountDiscoveryActions.discoverInstitutionLoginWithAccountsResponse(
                  {
                    institutionLogin: institutionLoginWithDiscoverId,
                    accountDiscoveryData: response.accountDiscoveryData,
                    updateStrategy: 'REPLACE',
                  },
                  { scope },
                ),
              );
            })
            .catch((error) => {
              assert(false, error);
            });
        })
        .catch((error) => {
          assert(false, error);
        });
    }
  }, [page, aggregator, institution, plaidWidget, dispatch, scope, plaidEnv]);

  // it's just a POC FF protected - not ready for production use
  const accountsFinicityWidgetExperiment = useAppSelector((state) =>
    featureFlagsSelectors.getFeatureFlag(state, 'accountsFinicityWidgetExperiment'),
  );
  useEffect(() => {
    if (page === 'WIDGET' && aggregator === 'FINICITY' && accountsFinicityWidgetExperiment) {
      new Promise((resolve, reject) =>
        dispatch(
          accountDiscoveryActions.getAggregationInfo(
            {
              institution,
              channel: 'FINICITY',
            },
            { resolve, reject, axiosConfig: { params: { connectWidget: true } } },
          ),
        ),
      )
        .then((aggregationInfo) => {
          assert(aggregationInfo.connectUrl, 'Missing connectUrl in aggregationInfo', aggregationInfo);
          const url = new URL(aggregationInfo.connectUrl);
          if (window.location.hostname === 'localhost') {
            url.hostname = 'localhost';
            url.port = '8000';
            url.protocol = 'http:';
          }
          window.open(url.toString(), '_blank', 'noopener,noreferrer');

          const institutionLogin = mkNewInstitutionLogin(aggregator, institution, { channel: 'FINICITY' });
          new Promise((resolve, reject) =>
            dispatch(
              accountDiscoveryActions.discoverInstitutionLoginWithAccountsByPolling(
                { institutionLogin, pollingRef: aggregationInfo.pollingRef },
                { resolve, reject },
              ),
            ),
          )
            .then((response) => {
              log.info('Finicity account discovery complete', JSON.stringify(response.accountDiscoveryData, null, 2));
              const institutionLoginWithDiscoverId = institutionLogin.set(
                'credentials',
                List([
                  institutionLoginsTypes.mkCredential({
                    key: 'discover_login_id',
                    value: response.institutionLogin.id,
                  }),
                ]),
              );
              dispatch(
                accountDiscoveryActions.discoverInstitutionLoginWithAccountsResponse(
                  {
                    institutionLogin: institutionLoginWithDiscoverId,
                    accountDiscoveryData: response.accountDiscoveryData,
                    updateStrategy: 'REPLACE',
                  },
                  { scope },
                ),
              );
            })
            .catch((error) => {
              assert(false, error);
            });
        })
        .catch((error) => {
          assert(false, error);
        });
    }
  }, [page, aggregator, institution, accountsFinicityWidgetExperiment, dispatch, scope]);

  return (
    <>
      {page === 'WIDGET' && aggregator === 'FINICITY' && !accountsFinicityWidgetExperiment && renderFinicity()}
      {page === 'WIDGET' && aggregator === 'PLAID' && !plaidWidget && renderPlaid()}
      {page === 'WIDGET' && aggregator === 'STRIPE' && renderStripe()}
      {page === 'NEW_OR_REDISCOVER_PICKER' && (
        <NewOrDiscoverForm
          existingLoginsAtInstitution={cpSuccessData.existingLoginsAtInstitution}
          fiBrandingIndent={fiBrandingIndent}
          onAdd={handleAdd}
          onBack={onBack}
          onRediscover={handleRediscover}
          ref={pageRef}
        />
      )}
    </>
  );
});
WidgetAuthenticator.displayName = 'WidgetAuthenticator';
WidgetAuthenticator.propTypes = {
  aggregator: PropTypes.string.isRequired,
  fiBrandingIndent: PropTypes.bool,
  institution: PropTypes.object.isRequired,
  institutionLoginToUpdate: PropTypes.object,
  onBack: PropTypes.func.isRequired,
  reauthenticate: PropTypes.bool,
  scope: PropTypes.string.isRequired,
  trackingProperties: PropTypes.object,
  fixFiMode: PropTypes.bool,
  businessId: PropTypes.string,
};

export default WidgetAuthenticator;
